/**
 * DOM measurement for page delimitation: reads container and section refs (getBoundingClientRect).
 * No splitable selectors here; use useListSplittables for data-splitable-*.
 */

export interface SplitableHeights {
  headerHeight: number;
  itemHeights: number[];
  itemGapPx: number;
  footerHeight: number;
}

/** Section heights + body start per column (first block top from container). */
export interface BlocksMeasurement {
  sectionHeights: number[][];
  /** Per-column: top of first block in that column (from container). Use this for offset math so each column matches DOM. */
  bodyStartPxPerColumn: number[];
}

/** Full measurement for layout: blocks + splitable heights by key. */
export interface PageBlocksMeasurement extends BlocksMeasurement {
  splitableByKey: Map<string, SplitableHeights>;
}

/**
 * Reads section heights and body start from the DOM. No querySelector, no splitable.
 * When scale !== 1 (e.g. container inside transform: scale()), converts to logical pixels.
 */
export function measurePageBlocks(
  container: HTMLDivElement,
  refs: (HTMLElement | null)[][],
  scale: number = 1,
): BlocksMeasurement | null {
  const containerRect = container.getBoundingClientRect();
  const toLogical = (v: number) => (scale !== 1 ? v / scale : v);

  const result = refs.reduce<{
    heights: number[][];
    bodyStartPxPerColumn: number[];
    ok: boolean;
  }>(
    (acc, columnRefs, col) => {
      if (!columnRefs?.length) return acc;
      const columnResult = columnRefs.reduce<{ heights: number[]; firstTop: number } | null>(
        (colAcc, el, i) => {
          if (!el) return null;
          const rect = el.getBoundingClientRect();
          const firstTop = i === 0 ? toLogical(rect.top - containerRect.top) : colAcc!.firstTop;
          return {
            heights: [...(colAcc?.heights ?? []), toLogical(rect.height)],
            firstTop,
          };
        },
        null,
      );
      if (columnResult === null) return { ...acc, ok: false };
      acc.heights[col] = columnResult.heights;
      acc.bodyStartPxPerColumn[col] = columnResult.firstTop;
      return acc;
    },
    { heights: [], bodyStartPxPerColumn: [], ok: true },
  );

  if (!result.ok || result.bodyStartPxPerColumn.length === 0) return null;

  return { sectionHeights: result.heights, bodyStartPxPerColumn: result.bodyStartPxPerColumn };
}
